import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    Avatar,
    FormControlLabel,
    Switch,
    MenuItem,
    Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, MonetizationOn as CoinIcon } from '@mui/icons-material';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import UpdateCategoryRequest from '../models/dtos/UpdateCategoryRequest';
import CreateCategoryRequest from '../models/dtos/CreateCategoryRequest';

interface CategoryEditModalProps {
    open: boolean;
    category: FlashBetCategoryDto | null;
    sportId: number;
    onSave: (data: UpdateCategoryRequest | CreateCategoryRequest) => void;
    onClose: () => void;
    isEdit: boolean;
}

const CategoryEditModal: React.FC<CategoryEditModalProps> = ({ 
    open, 
    category, 
    sportId, 
    onSave, 
    onClose, 
    isEdit 
}) => {
    const [formData, setFormData] = useState({
        name: '',
        photoUrl: '',
        smallLogoUrl: '',
        subtitle: '',
        localizations: {} as { [key: string]: string },
        subtitleLocalizations: {} as { [key: string]: string },
        enabled: true,
        entryFee: 100
    });
    const [localizationEntries, setLocalizationEntries] = useState<Array<{ lang: string; name: string }>>([]);
    const [subtitleLocalizationEntries, setSubtitleLocalizationEntries] = useState<Array<{ lang: string; name: string }>>([]);
    const [errors, setErrors] = useState<{ name?: string; photoUrl?: string; entryFee?: string }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (isEdit && category) {
            setFormData({
                name: category.name,
                photoUrl: category.photoUrl || '',
                smallLogoUrl: category.smallLogoUrl || '',
                subtitle: category.subtitle || '',
                localizations: category.localizations || {},
                subtitleLocalizations: category.subtitleLocalizations || {},
                enabled: category.enabled,
                entryFee: category.entryFee || 100
            });
            
            // Convert localizations to array format for editing
            const entries = Object.entries(category.localizations || {}).map(([lang, name]) => ({
                lang,
                name
            }));
            setLocalizationEntries(entries);
            
            // Convert subtitle localizations to array format for editing
            const subtitleEntries = Object.entries(category.subtitleLocalizations || {}).map(([lang, name]) => ({
                lang,
                name
            }));
            setSubtitleLocalizationEntries(subtitleEntries);
        } else {
            // Reset form for add mode
            setFormData({
                name: '',
                photoUrl: '',
                smallLogoUrl: '',
                subtitle: '',
                localizations: {},
                subtitleLocalizations: {},
                enabled: true,
                entryFee: 100
            });
            setLocalizationEntries([]);
            setSubtitleLocalizationEntries([]);
        }
        setErrors({});
    }, [category, open, isEdit]);

    const validateForm = () => {
        const newErrors: { name?: string; photoUrl?: string; entryFee?: string } = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        // PhotoUrl is now optional, so we don't validate it
        
        if (formData.entryFee < 100 || formData.entryFee > 1000000000) {
            newErrors.entryFee = 'Entry fee must be between 100 and 1,000,000,000';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        // Convert localization entries back to object format
        const localizations: { [key: string]: string } = {};
        localizationEntries.forEach(entry => {
            if (entry.lang.trim() && entry.name.trim()) {
                localizations[entry.lang.trim()] = entry.name.trim();
            }
        });
        
        // Convert subtitle localization entries back to object format
        const subtitleLocalizations: { [key: string]: string } = {};
        subtitleLocalizationEntries.forEach(entry => {
            if (entry.lang.trim() && entry.name.trim()) {
                subtitleLocalizations[entry.lang.trim()] = entry.name.trim();
            }
        });

        try {
            if (isEdit) {
                const dataToSave: UpdateCategoryRequest = {
                    name: formData.name.trim(),
                    photoUrl: formData.photoUrl.trim() || null,
                    smallLogoUrl: formData.smallLogoUrl.trim() || null,
                    subtitle: formData.subtitle.trim() || null,
                    localizations,
                    subtitleLocalizations,
                    enabled: formData.enabled,
                    entryFee: formData.entryFee
                };
                onSave(dataToSave);
            } else {
                const dataToSave: CreateCategoryRequest = {
                    name: formData.name.trim(),
                    photoUrl: formData.photoUrl.trim() || null,
                    smallLogoUrl: formData.smallLogoUrl.trim() || null,
                    subtitle: formData.subtitle.trim() || null,
                    localizations,
                    subtitleLocalizations,
                    sportId: sportId,
                    enabled: formData.enabled,
                    entryFee: formData.entryFee
                };
                onSave(dataToSave);
            }
        } catch (error) {
            console.error('Failed to save category:', error);
            setErrorMessage(`Failed to ${isEdit ? 'update' : 'create'} category. Please try again.`);
        }
    };

    const handleInputChange = (field: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        let value: string | number | boolean;
        if (field === 'enabled') {
            value = event.target.checked;
        } else if (field === 'entryFee') {
            value = parseInt(event.target.value) || 0;
        } else {
            value = event.target.value;
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const addLocalizationEntry = () => {
        setLocalizationEntries(prev => [...prev, { lang: '', name: '' }]);
    };

    const removeLocalizationEntry = (index: number) => {
        setLocalizationEntries(prev => prev.filter((_, i) => i !== index));
    };

    const updateLocalizationEntry = (index: number, field: 'lang' | 'name', value: string) => {
        setLocalizationEntries(prev => prev.map((entry, i) => 
            i === index ? { ...entry, [field]: value } : entry
        ));
    };

    const addSubtitleLocalizationEntry = () => {
        setSubtitleLocalizationEntries(prev => [...prev, { lang: '', name: '' }]);
    };

    const removeSubtitleLocalizationEntry = (index: number) => {
        setSubtitleLocalizationEntries(prev => prev.filter((_, i) => i !== index));
    };

    const updateSubtitleLocalizationEntry = (index: number, field: 'lang' | 'name', value: string) => {
        setSubtitleLocalizationEntries(prev => prev.map((entry, i) => 
            i === index ? { ...entry, [field]: value } : entry
        ));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEdit ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Category Name *"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                margin="normal"
                            />
                            
                            <TextField
                                fullWidth
                                label="Subtitle"
                                value={formData.subtitle}
                                onChange={handleInputChange('subtitle')}
                                margin="normal"
                                helperText="Optional subtitle that appears below the category name"
                            />
                            
                            <TextField
                                fullWidth
                                label="Photo URL"
                                value={formData.photoUrl}
                                onChange={handleInputChange('photoUrl')}
                                error={!!errors.photoUrl}
                                helperText={errors.photoUrl || "Optional main image URL for the category"}
                                margin="normal"
                            />
                            
                            <TextField
                                fullWidth
                                label="Small Logo URL"
                                value={formData.smallLogoUrl}
                                onChange={handleInputChange('smallLogoUrl')}
                                margin="normal"
                                helperText="Optional small logo URL that appears in the top right corner of the category card"
                            />
                            
                            <TextField
                                fullWidth
                                label="Entry Fee *"
                                type="number"
                                value={formData.entryFee}
                                onChange={handleInputChange('entryFee')}
                                error={!!errors.entryFee}
                                helperText={errors.entryFee || 'Entry fee must be between 100 and 1,000,000,000'}
                                margin="normal"
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                            <CoinIcon color="primary" />
                                        </Box>
                                    ),
                                }}
                                inputProps={{
                                    min: 100,
                                    max: 1000000000
                                }}
                            />
                            
                            <Box mt={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.enabled}
                                            onChange={handleInputChange('enabled')}
                                        />
                                    }
                                    label="Enabled"
                                />
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={2}>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                    <Typography variant="body2" color="text.secondary" mb={1}>
                                        Main Photo Preview
                                    </Typography>
                                    <Avatar
                                        src={formData.photoUrl || undefined}
                                        alt={formData.name}
                                        sx={{ width: 80, height: 80 }}
                                    />
                                </Box>
                                
                                {formData.smallLogoUrl && (
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Small Logo Preview
                                        </Typography>
                                        <Avatar
                                            src={formData.smallLogoUrl}
                                            alt="Small Logo"
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    <Box mt={3}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h6">Localizations</Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addLocalizationEntry}
                                variant="outlined"
                                size="small"
                            >
                                Add Language
                            </Button>
                        </Box>
                        
                        {localizationEntries.map((entry, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
                                <TextField
                                    label="Language Code"
                                    select
                                    value={entry.lang}
                                    onChange={(e) => updateLocalizationEntry(index, 'lang', e.target.value)}
                                    size="small"
                                    sx={{ width: '150px' }}
                                >
                                    <MenuItem value="">Select language</MenuItem>
                                    <MenuItem value="de">German (de)</MenuItem>
                                    <MenuItem value="fr">French (fr)</MenuItem>
                                    <MenuItem value="it">Italian (it)</MenuItem>
                                    <MenuItem value="ro">Romanian (ro)</MenuItem>
                                    <MenuItem value="ru">Russian (ru)</MenuItem>
                                    <MenuItem value="es">Spanish (es)</MenuItem>
                                    <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                    <MenuItem value="pl">Polish (pl)</MenuItem>
                                    <MenuItem value="tr">Turkish (tr)</MenuItem>
                                </TextField>
                                <TextField
                                    label="Localized Name"
                                    value={entry.name}
                                    onChange={(e) => updateLocalizationEntry(index, 'name', e.target.value)}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                    onClick={() => removeLocalizationEntry(index)}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        
                        {localizationEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No localizations added. Click "Add Language" to add localized names.
                            </Typography>
                        )}
                    </Box>

                    <Box mt={3}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h6">Subtitle Localizations</Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addSubtitleLocalizationEntry}
                                variant="outlined"
                                size="small"
                            >
                                Add Language
                            </Button>
                        </Box>
                        
                        {subtitleLocalizationEntries.map((entry, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
                                <TextField
                                    label="Language Code"
                                    select
                                    value={entry.lang}
                                    onChange={(e) => updateSubtitleLocalizationEntry(index, 'lang', e.target.value)}
                                    size="small"
                                    sx={{ width: '150px' }}
                                >
                                    <MenuItem value="">Select language</MenuItem>
                                    <MenuItem value="de">German (de)</MenuItem>
                                    <MenuItem value="fr">French (fr)</MenuItem>
                                    <MenuItem value="it">Italian (it)</MenuItem>
                                    <MenuItem value="ro">Romanian (ro)</MenuItem>
                                    <MenuItem value="ru">Russian (ru)</MenuItem>
                                    <MenuItem value="es">Spanish (es)</MenuItem>
                                    <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                    <MenuItem value="pl">Polish (pl)</MenuItem>
                                    <MenuItem value="tr">Turkish (tr)</MenuItem>
                                </TextField>
                                <TextField
                                    label="Localized Subtitle"
                                    value={entry.name}
                                    onChange={(e) => updateSubtitleLocalizationEntry(index, 'name', e.target.value)}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                    onClick={() => removeSubtitleLocalizationEntry(index)}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        
                        {subtitleLocalizationEntries.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No subtitle localizations added. Click "Add Language" to add localized subtitles.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">
                    {isEdit ? 'Save Changes' : 'Create Category'}
                </Button>
            </DialogActions>

            {/* Error Toast */}
            <Snackbar
                open={errorMessage !== null}
                autoHideDuration={4000}
                onClose={() => setErrorMessage(null)}
                message={errorMessage}
            />
        </Dialog>
    );
};

export default CategoryEditModal;